import React, { useContext, useEffect, useMemo } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { CustomDropdown } from "../../Common/CustomDropdown";
import ThemeContext from "../../../context/ThemeProvider";
import {
  DATE_FORMAT,
  DROPDOWN,
  TOTAL_SCORE_LABEL,
  CHART,
} from "./WheelOfLifeChartAlt.constants";
import { RootBox, HeaderBox, ChartBox, ScoreText } from "./WheelOfLifeChartAlt.styles";

am4core.useTheme(am4themes_animated);

const WheelOfLifeChartAlt = ({ chartData, date, setDate }) => {
  const theme = useTheme();
  const { wheelOfLifeData } = useSelector((state) => state.user);
  const { selectedTheme } = useContext(ThemeContext);

  const markedDates = useMemo(() => {
    if (!wheelOfLifeData?.length) return [];
    return wheelOfLifeData.map((entry) => ({
      label: dayjs(entry?.date).format(DATE_FORMAT),
      value: dayjs(entry?.date).format(DATE_FORMAT),
    }));
  }, [wheelOfLifeData]);

  const selectedEntry = useMemo(() => {
    if (!wheelOfLifeData?.length) return null;
    return wheelOfLifeData.find((entry) =>
      dayjs(entry?.date).isSame(date, "day")
    ) ?? null;
  }, [wheelOfLifeData, date]);

  useEffect(() => {
    const chart = am4core.create(CHART.ID, am4charts.RadarChart);
    chart.hiddenState.properties.opacity = 0;
    chart.data = chartData;
    chart.padding(CHART.PADDING, CHART.PADDING, CHART.PADDING, CHART.PADDING);
    chart.logo.disabled = true;

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "label";
    categoryAxis.renderer.labels.template.location = 0.5;
    categoryAxis.renderer.labels.template.fontSize = CHART.LABEL_FONT_SIZE;
    categoryAxis.renderer.labels.template.fill = am4core.color(
      theme.palette.text.primary
    );
    categoryAxis.renderer.grid.template.stroke = am4core.color(
      theme.palette.text.primary
    );

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = CHART.VALUE_AXIS_MIN;
    valueAxis.max = CHART.VALUE_AXIS_MAX;
    valueAxis.renderer.labels.template.disabled = true;
    valueAxis.renderer.grid.template.stroke = am4core.color(
      theme.palette.text.primary
    );

    const series = chart.series.push(new am4charts.RadarColumnSeries());
    series.columns.template.tooltipText = "{label}: {valueY.value}";
    series.columns.template.width = am4core.percent(100);
    series.columns.template.strokeWidth = 0;
    series.columns.template.adapter.add("fill", (fill, target) =>
      target.dataItem ? target.dataItem.dataContext.color : fill
    );
    series.dataFields.categoryX = "label";
    series.dataFields.valueY = "value";

    return () => chart.dispose();
  }, [chartData, selectedTheme, theme.palette.text.primary]);

  return (
    <RootBox>
      <HeaderBox>
        <CustomDropdown
          value={dayjs(date).format(DATE_FORMAT)}
          setValue={setDate}
          label={DROPDOWN.LABEL}
          data={markedDates}
          noIcon
          placeholder={DROPDOWN.PLACEHOLDER}
          width={DROPDOWN.WIDTH}
        />
        {selectedEntry?.totalScore != null && (
          <ScoreText>
            {TOTAL_SCORE_LABEL}
            {selectedEntry.totalScore}
          </ScoreText>
        )}
      </HeaderBox>

      <ChartBox id={CHART.ID} />
    </RootBox>
  );
};

export default WheelOfLifeChartAlt;
